import React, { useState, useEffect } from 'react';
import { 
  Search, 
  CheckCircle, 
  AlertTriangle, 
  Info, 
  Brain, 
  Target,
  Eye,
  ThumbsUp,
  ThumbsDown,
  ArrowRight
} from 'lucide-react';
import { ColumnDetector, ColumnSuggestion, DetectionResult } from '../utils/columnDetection';

interface ColumnSelectorProps {
  headers: string[];
  sampleData: Record<string, any>[];
  onColumnSelect: (column: string) => void;
  onCancel: () => void;
  currentSelection?: string;
}

export function ColumnSelector({ 
  headers, 
  sampleData, 
  onColumnSelect, 
  onCancel, 
  currentSelection 
}: ColumnSelectorProps) {
  const [detectionResult, setDetectionResult] = useState<DetectionResult | null>(null);
  const [selectedColumn, setSelectedColumn] = useState<string>(currentSelection || '');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAllColumns, setShowAllColumns] = useState(false);

  useEffect(() => {
    const result = ColumnDetector.detectColumns(headers, sampleData);
    setDetectionResult(result);
    
    // استخدم الاقتراح المرسل من FileUpload أو احصل على أفضل اقتراح
    if (currentSelection) {
      setSelectedColumn(currentSelection);
    } else {
      const bestSuggestion = ColumnDetector.suggestBestWorkshopColumn(result);
      if (bestSuggestion) {
        setSelectedColumn(bestSuggestion.columnName);
      }
    }
  }, [headers, sampleData, currentSelection]);

  const getConfidenceColor = (confidence: number): string => {
    if (confidence >= 0.8) return 'text-green-600 bg-green-50 border-green-200';
    if (confidence >= 0.6) return 'text-blue-600 bg-blue-50 border-blue-200';
    if (confidence >= 0.4) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getConfidenceIcon = (confidence: number) => {
    if (confidence >= 0.8) return <CheckCircle className="w-5 h-5" />;
    if (confidence >= 0.6) return <ThumbsUp className="w-5 h-5" />;
    if (confidence >= 0.4) return <Info className="w-5 h-5" />;
    return <AlertTriangle className="w-5 h-5" />;
  };

  const getConfidenceText = (confidence: number): string => {
    if (confidence >= 0.8) return 'ثقة عالية جداً';
    if (confidence >= 0.6) return 'ثقة عالية';
    if (confidence >= 0.4) return 'ثقة متوسطة';
    return 'ثقة منخفضة';
  };

  const filteredHeaders = headers.filter(header =>
    header.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleConfirm = () => {
    if (selectedColumn) {
      onColumnSelect(selectedColumn);
    }
  };

  const getSampleValues = (columnName: string): string[] => {
    return sampleData
      .slice(0, 5)
      .map(row => row[columnName])
      .filter(val => val != null && val !== '')
      .map(val => String(val).substring(0, 30));
  };

  if (!detectionResult) {
    return <div className="p-6 text-center">جاري تحليل الأعمدة...</div>;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" dir="rtl">
      <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-y-auto p-6 m-4 w-full">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <Brain className="w-7 h-7 text-blue-600" />
            اختيار عمود الورش/المجموعات بذكاء
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700 text-xl font-bold"
          >
            ✕
          </button>
        </div>

        {/* معلومات التحليل */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <Target className="w-6 h-6 text-blue-600" />
            <h3 className="text-lg font-semibold text-blue-800">نتائج التحليل الذكي</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-white p-3 rounded border">
              <p className="font-medium text-gray-700">أعمدة مرشحة للورش</p>
              <p className="text-xl font-bold text-blue-600">{detectionResult.workshopColumns.length}</p>
            </div>
            <div className="bg-white p-3 rounded border">
              <p className="font-medium text-gray-700">أعمدة أخرى للتجميع</p>
              <p className="text-xl font-bold text-green-600">{detectionResult.groupingColumns.length}</p>
            </div>
            <div className="bg-white p-3 rounded border">
              <p className="font-medium text-gray-700">أعمدة الأسئلة</p>
              <p className="text-xl font-bold text-purple-600">{detectionResult.questionColumns.length}</p>
            </div>
          </div>
        </div>

        {/* الاقتراحات الذكية */}
        {detectionResult.workshopColumns.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-green-800 mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              الأعمدة المرشحة للورش (مرتبة حسب الثقة)
            </h3>
            <div className="space-y-3">
              {detectionResult.workshopColumns.map((suggestion) => (
                <div
                  key={suggestion.columnName}
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    selectedColumn === suggestion.columnName
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                  onClick={() => setSelectedColumn(suggestion.columnName)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        checked={selectedColumn === suggestion.columnName}
                        onChange={() => setSelectedColumn(suggestion.columnName)}
                        className="w-4 h-4"
                      />
                      <h4 className="font-semibold text-gray-800">{suggestion.columnName}</h4>
                    </div>
                    <div className={`flex items-center gap-2 px-3 py-1 rounded-full border ${getConfidenceColor(suggestion.confidence)}`}>
                      {getConfidenceIcon(suggestion.confidence)}
                      <span className="text-sm font-medium">
                        {getConfidenceText(suggestion.confidence)} ({Math.round(suggestion.confidence * 100)}%)
                      </span>
                    </div>
                  </div>
                  
                  <div className="text-sm text-gray-600 mb-3">
                    <p className="font-medium mb-1">أسباب الترشيح:</p>
                    <ul className="space-y-1">
                      {suggestion.reasons.map((reason, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <ArrowRight className="w-3 h-3 mt-1 text-blue-500 flex-shrink-0" />
                          <span>{reason}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-sm font-medium text-gray-700 mb-2">عينة من البيانات:</p>
                    <div className="flex flex-wrap gap-2">
                      {getSampleValues(suggestion.columnName).map((value, index) => (
                        <span
                          key={index}
                          className="bg-white px-2 py-1 rounded border text-xs text-gray-600"
                        >
                          {value}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* الأعمدة الأخرى المحتملة */}
        {detectionResult.groupingColumns.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-blue-800 mb-4 flex items-center gap-2">
              <Info className="w-5 h-5" />
              أعمدة أخرى قد تكون مناسبة للتجميع
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {detectionResult.groupingColumns.map((suggestion) => (
                <div
                  key={suggestion.columnName}
                  className={`border rounded-lg p-3 cursor-pointer transition-all ${
                    selectedColumn === suggestion.columnName
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                  onClick={() => setSelectedColumn(suggestion.columnName)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={selectedColumn === suggestion.columnName}
                        onChange={() => setSelectedColumn(suggestion.columnName)}
                        className="w-4 h-4"
                      />
                      <h4 className="font-medium text-gray-800">{suggestion.columnName}</h4>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${getConfidenceColor(suggestion.confidence)}`}>
                      {Math.round(suggestion.confidence * 100)}%
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {suggestion.reasons.slice(0, 2).join(' • ')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* البحث في جميع الأعمدة */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">البحث في جميع الأعمدة</h3>
            <button
              onClick={() => setShowAllColumns(!showAllColumns)}
              className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              {showAllColumns ? 'إخفاء' : 'عرض'} جميع الأعمدة
            </button>
          </div>
          
          <div className="relative mb-4">
            <Search className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="ابحث في أسماء الأعمدة..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-4 pr-10 py-2 border rounded-lg text-right"
              dir="rtl"
            />
          </div>

          {(showAllColumns || searchTerm) && (
            <div className="max-h-60 overflow-y-auto border rounded-lg">
              {filteredHeaders.map((header) => (
                <div
                  key={header}
                  className={`p-3 border-b last:border-b-0 cursor-pointer transition-colors ${
                    selectedColumn === header
                      ? 'bg-blue-50 border-blue-200'
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setSelectedColumn(header)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        checked={selectedColumn === header}
                        onChange={() => setSelectedColumn(header)}
                        className="w-4 h-4"
                      />
                      <span className="font-medium">{header}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      عينة: {getSampleValues(header).slice(0, 2).join(', ')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* معاينة العمود المحدد */}
        {selectedColumn && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <h4 className="font-semibold text-green-800 mb-3">معاينة العمود المحدد: {selectedColumn}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">عينة من البيانات:</p>
                <div className="space-y-1">
                  {getSampleValues(selectedColumn).map((value, index) => (
                    <div key={index} className="bg-white p-2 rounded border text-sm">
                      {value || '(قيمة فارغة)'}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">معلومات إضافية:</p>
                <div className="bg-white p-3 rounded border text-sm space-y-1">
                  <p>عدد القيم الفريدة: {new Set(getSampleValues(selectedColumn)).size}</p>
                  <p>إجمالي العينة: {getSampleValues(selectedColumn).length}</p>
                  <p>نوع العمود: {detectionResult.workshopColumns.find(c => c.columnName === selectedColumn)?.category || 'عام'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* أزرار التحكم */}
        <div className="flex justify-between items-center pt-4 border-t">
          <button
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
          >
            إلغاء
          </button>
          
          <button
            onClick={handleConfirm}
            disabled={!selectedColumn}
            className={`px-8 py-3 rounded-lg font-semibold flex items-center gap-2 ${
              selectedColumn
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            }`}
          >
            <CheckCircle className="w-5 h-5" />
            تأكيد الاختيار
          </button>
        </div>
      </div>
    </div>
  );
} 